/* ============================================================
   SINGSPACE ADMIN - MODALE PRÉVISUALISATION DEVIS
   Aperçu PDF (iframe vers API) + téléchargement + envoi email
   ============================================================ */

'use client';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Download, Loader2, Send, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';

interface EmailPreviewModalProps {
  open: boolean;
  onClose: () => void;
  onConfirmSend: () => Promise<void>;
  booking: any;
}

export function EmailPreviewModal({ open, onClose, onConfirmSend, booking }: EmailPreviewModalProps) {
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [sending, setSending] = useState(false);

  // Construire l'URL du PDF (iframe vers l'API serveur)
  useEffect(() => {
    if (!open || !booking) {
      setPdfUrl(null);
      return;
    }

    const token = booking.verificationToken;
    if (!token) {
      toast.error('Token de vérification manquant pour le PDF');
      setPdfUrl(null);
      return;
    }

    // L'API va générer le PDF avec le bon titre (DEVIS ou FACTURE PRO FORMA)
    // On ajoute inline=true pour éviter le téléchargement forcé dans l'iframe
    const url = `/api/bookings/${booking.id}/pro-forma-pdf?token=${token}&inline=true`;
    setPdfUrl(url);
  }, [open, booking]);

  const handleDownload = () => {
    if (!booking?.verificationToken) {
      toast.error('Impossible de télécharger le PDF');
      return;
    }
    // Ouvrir dans un nouvel onglet pour téléchargement
    const url = `/api/bookings/${booking.id}/pro-forma-pdf?token=${booking.verificationToken}`;
    window.open(url, '_blank');
  };

  const handleSend = async () => {
    setSending(true);
    try {
      await onConfirmSend();
      onClose();
      toast.success('Devis envoyé avec succès');
    } catch (error) {
      console.error('[EmailPreviewModal] Error sending quote:', error);
      toast.error("Erreur lors de l'envoi");
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl h-[90vh] flex flex-col p-0 gap-0 overflow-hidden outline-none font-sans">
        <DialogHeader className="p-6 pb-2">
          <DialogTitle>Prévisualisation du devis</DialogTitle>
          <DialogDescription>Vérifiez le devis avant envoi au client</DialogDescription>
        </DialogHeader>

        {/* Aperçu PDF dans iframe */}
        <div className="flex-1 w-full border-y relative bg-slate-50">
          {!booking?.verificationToken ? (
            <div className="flex items-center justify-center h-full text-slate-500 text-sm">
              Token de vérification manquant
            </div>
          ) : (
            pdfUrl && (
              <iframe src={pdfUrl} className="absolute inset-0 w-full h-full border-0" title="Prévisualisation devis" />
            )
          )}
        </div>

        <DialogFooter className="p-6 gap-2 sm:gap-0">
          <Button variant="outline" onClick={onClose} disabled={sending}>
            <X className="w-4 h-4 mr-2" />
            Annuler
          </Button>
          <Button variant="secondary" onClick={handleDownload} disabled={!booking?.verificationToken || sending}>
            <Download className="w-4 h-4 mr-2" />
            Télécharger le PDF
          </Button>
          <Button onClick={handleSend} disabled={!booking?.verificationToken || sending}>
            {sending ? (
              <>
                <Loader2 className="w-4 h-4 mr-2" />
                Envoi...
              </>
            ) : (
              <>
                <Send className="w-4 h-4 mr-2" />
                Envoyer par email
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
